/**
 * Cloud shadows on the ground: the cloud field, projected sun-aligned onto the terrain.
 *
 * ## Why this is a 2D projection and not a shadow map
 *
 * The obvious implementation renders the cloud layer into a depth or opacity map from the sun's
 * point of view and samples it like any other shadow. It is also the wrong shape of solution here.
 * The layer is a *volume*, so an opacity map means marching it a second time, from a second
 * viewpoint, over a footprint that has to cover the whole shadow-casting area — which for an
 * 8 km cascade set at a 30° sun is a 16 km square. That is a second raymarch of comparable cost
 * to the first, for an effect that is inherently low-frequency: a cumulus shadow on the ground is
 * a soft blob a kilometre across with a penumbra hundreds of metres wide.
 *
 * So instead: for each shaded fragment, walk *one* ray from the surface to the middle of the cloud
 * layer along the sun direction, and evaluate the same density field the march evaluates, at that
 * one point. Two texture fetches, no extra pass, no extra target, and — because it reads the same
 * volume with the same wind offset and the same coverage remap — the shadow is under the cloud
 * that is actually casting it, and drifts downwind with it, automatically. Nothing has to be kept
 * in sync, because there is only one field.
 *
 * What this cannot do is a shadow cast by cloud the fragment cannot "see" through the layer's
 * middle — it samples one height rather than integrating the column. For cumulus, whose horizontal
 * extent is several times the layer thickness, the difference is not visible.
 *
 * ## Where the softness comes from
 *
 * Two places, and neither is a blur:
 *
 *   1. **The field's own texel scale.** One repeat of the shape volume covers 9 km across 64
 *      texels, so the trilinear filter band-limits the sampled field at about 140 m. That is
 *      already close to a real penumbra: the sun's 0.53° disc alone spreads a cloud edge at
 *      4000 ft over ~11 m, and the ragged three-dimensional edge of the cloud itself spreads it
 *      over a few hundred.
 *   2. **Distance to the caster.** A shadow softens the further the light has travelled since it
 *      was occluded, so the density is blended toward the layer's local *mean* opacity as the
 *      projection distance grows. It costs nothing — the mean is `localCoverage`, which is already
 *      computed — and it is what stops shadows cast from a low sun 20 km away from being as crisp
 *      as one from directly overhead.
 *
 * ## What it modulates, and what it deliberately does not
 *
 * The **direct sun term only**, through `NodeMaterial.receivedShadowNode`. Skylight is untouched,
 * which is the entire reason a real cloud shadow is blue-grey rather than black: the ground under
 * a cumulus is still lit by the whole hemisphere. Multiplying the albedo instead — the cheap
 * shortcut — darkens the ambient too, and produces a shadow that goes black at dusk when it should
 * be all but invisible.
 *
 * That hook lives inside three's `ShadowNode.setup`, so it runs only where the sun is actually
 * casting shadows. That is not a limitation in practice and it is checked by a test: `castShadows`
 * is set at High and Ultra, and both of those tiers have `shadows.enabled`.
 *
 * ## Units
 *
 * Positions are render-space metres (x east, y up, z south). The layer's altitudes are metres
 * above mean sea level, and render `y` is tangent-plane height — they differ by the curvature drop,
 * which is 49 m at 25 km from the origin and moves the projected sample point by less than 100 m
 * against a field whose features are 9 km across. Not corrected, deliberately.
 *
 * `updateFixed` / `updateFrame`: neither. It is a set of uniforms written by
 * {@link VolumetricClouds}, which owns the values, in the same calls that push them to the march.
 */

import { Vector3, type Data3DTexture } from 'three';
import type { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn,
  If,
  clamp,
  exp,
  float,
  max,
  min,
  mix,
  positionWorld,
  smoothstep,
  texture3D,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import { remap, sampleVolume } from './cloudNoise.js';
import {
  DENSITY_BAND,
  coverageOpacity,
  coverageOpacityNode,
  coverageThreshold,
  coverageThresholdNode,
} from './coverage.js';

/**
 * One repeat of the shape volume, in metres. Must match `volumetricClouds.sampleDensity`.
 *
 * Not a shared constant with that file on purpose: it is *this* module that would break silently
 * if they diverged (the shadow would sit next to the cloud instead of under it), so the number is
 * written here with the reason attached, and `cloudShadow.test.ts` pins the two together.
 */
const SAMPLE_SCALE = 1 / 9_000;

/** The horizontal slice of the shape volume the march reads its coverage field from. */
const COVERAGE_SLICE_Y = 0.37;

/**
 * Height fractions at which the column is sampled, and where they land in the volume.
 *
 * Three taps, not one. A single mid-layer sample is the obvious cheap choice and it is
 * *systematically* wrong: mid-layer is where the vertical gradient peaks, so multiplying it by the
 * full layer thickness overestimates the column — worst exactly where the layer is broken into
 * separate towers that do not span it, which is the case whose shadows matter most. Measured
 * against a 24-step integration of the same field, one tap correlates at r = 0.77–0.88 and needs a
 * calibration that swings by a factor of two across the weather presets; three taps correlate at
 * r = 0.86–0.97 and hold one calibration.
 *
 * `0.85` is the march's own vertical scale — it drives the volume's `y` coordinate from the height
 * fraction rather than from the planet-centred position, and this has to match or the shadow reads
 * a different part of the field from the cloud.
 */
const LAYER_TAPS = [0.2, 0.5, 0.8] as const;
const VERTICAL_SCALE = 0.85;

/**
 * The vertical gradient at the top tap, for the two shapes the march blends between.
 *
 * At h = 0.2 and h = 0.5 both `smoothstep` pairs saturate to exactly 1, so those taps carry no
 * gradient term at all. Only the top tap is inside a falling edge: `smoothstep(1, 0.62, 0.8)` is
 * 0.5387 for the cumulus profile and `smoothstep(1, 0.88, 0.8)` clamps to 1 for the stratus one.
 * Which is the physical point of the two profiles — a cumulus runs out of buoyancy near its top
 * and a stratus deck does not.
 */
const TOP_TAP_CUMULUS_GRADIENT = 0.538_7;

/**
 * Extinction per metre per unit density [1/m].
 *
 * The **view ray's** coefficient (`exp(density · stepLength · −0.012)`), not the light march's
 * softer 0.006. That is the choice that makes the shadow honest: the number that decides how dark
 * the ground goes is the same one that decides how much sky you can see through the layer, so a
 * cloud you cannot see through cannot leave a pale shadow, and one you can see through cannot
 * leave a black one.
 */
const EXTINCTION_PER_M = 0.012;

/**
 * What the detail-erosion octave the taps skip would have taken off.
 *
 * The march erodes the shaped field with a second, higher-frequency volume before integrating.
 * Reproducing that here would cost three more fetches to move a soft ground shadow by a fraction
 * of its own penumbra. Instead its *mean* effect is folded in as one constant, measured against
 * the full 24-step integration: 0.79 at scattered, 0.80 at broken, 0.44 at few, where erosion bites
 * hardest because there is least left to erode. 0.7 splits them, and the residual at few is an
 * over-estimate of a shadow that is gentle in absolute terms anyway (an optical depth of 0.33
 * rather than 0.20 — 72 % of the sun through instead of 82 %).
 */
const EROSION_ALLOWANCE = 0.7;

/**
 * Floor on the direct term.
 *
 * Real cloud does not extinguish direct sunlight completely; forward multiple scattering leaks a
 * few percent through even a thick deck, and driving the term to zero makes shadow cores read as
 * holes. Skylight is untouched regardless, so this is the floor on the *sun* only.
 */
const MIN_DIRECT = 0.06;

/**
 * Cap on the slant-path multiplier.
 *
 * The path through a layer of thickness `T` at sun elevation `e` is `T / sin(e)`, which diverges
 * at the horizon. Capped at four, which is a 14.5° sun — below that the sun is dim and reddened
 * enough that the shadow contrast has collapsed anyway, and the elevation gate is already fading
 * the whole effect out.
 */
const MAX_SLANT = 4;

/**
 * Sun elevations, as `sin(elevation)`, over which the effect fades in.
 *
 * Below 1.1° the projection distance to the layer runs to hundreds of kilometres and the sample
 * point stops having anything to do with the cloud overhead; there is also almost no direct
 * sunlight left to modulate. Fully on above 9.2°.
 */
const SUN_GATE_LO = 0.02;
const SUN_GATE_HI = 0.16;

/** Longest sun ray the projection will follow [m]. Beyond this the answer is noise. */
const MAX_PROJECTION_M = 40_000;

/** Projection distance over which the shadow blurs fully toward the layer's mean opacity [m]. */
const PENUMBRA_RANGE_M = 14_000;
/** How far toward that mean a maximally distant caster is allowed to blur. */
const MAX_PENUMBRA_BLEND = 0.6;

/** `remap`, as a node — the same interval map as `cloudNoise.remap`, evaluated on the GPU. */
function remapNode(
  value: Node<'float'>,
  low1: Node<'float'>,
  high1: Node<'float'>,
  low2: Node<'float'>,
  high2: Node<'float'>,
): Node<'float'> {
  return low2.add(value.sub(low1).mul(high2.sub(low2)).div(max(high1.sub(low1), float(1e-5))));
}

export interface CloudShadowOptions {
  /** The shape volume the raymarch samples. Shared, not copied — one upload, one field. */
  shape: Data3DTexture;
}

/**
 * Inputs to {@link cloudShadowFactor}, the CPU reference implementation.
 *
 * Every field is a plain number, so a test can assert the projection geometry without a GPU —
 * the same arrangement `shellInterval.test.ts` uses to pin the shell algebra.
 */
export interface CloudShadowInputs {
  /** Shaded point, render space [m]. */
  x: number;
  y: number;
  z: number;
  /** Unit vector toward the sun, render space. */
  sunX: number;
  sunY: number;
  sunZ: number;
  /** Cloud base [m AMSL] and layer thickness [m]. */
  baseAltitudeM: number;
  layerThicknessM: number;
  /** Sky coverage, 0…1, in the okta sense — the weather's own number. */
  coverage: number;
  /** How lumpy the layer is, 0…1 — cumulus towers at 1, a flat stratus lid at 0. */
  convection: number;
  /** Advection offset [m], x and z. The layer drifts; the sample coordinate moves with it. */
  windOffsetX: number;
  windOffsetZ: number;
  /** 0 disables the effect entirely; 1 is full strength. */
  amount: number;
  /** The shape volume's bytes and edge length. */
  shape: Uint8Array;
  shapeSize: number;
}

export interface CloudShadowResult {
  /** Fraction of direct sunlight reaching the point, 0…1. 1 means unshadowed. */
  factor: number;
  /** Where the sun ray crossed the middle of the layer, render space [m]. */
  hitX: number;
  hitZ: number;
  /** Distance along the sun ray to that crossing [m]. */
  projectionM: number;
  /** Mean density down the column there, 0…1 — what the optical depth was built from. */
  density: number;
}

/**
 * The shader's arithmetic, on the CPU.
 *
 * This is not used at runtime — the GPU does this per fragment. It exists so the projection
 * geometry, the elevation gate, the coverage remap and the Beer's-law falloff can each be asserted
 * directly, which is the only way any of them can be checked at all: a wrong cloud shadow looks
 * exactly like a right one until you know where the cloud is.
 */
export function cloudShadowFactor(inputs: CloudShadowInputs): CloudShadowResult {
  const {
    x, y, z, sunX, sunY, sunZ,
    baseAltitudeM, layerThicknessM, coverage, convection,
    windOffsetX, windOffsetZ, amount, shape, shapeSize,
  } = inputs;

  const gate = smoothstepScalar(SUN_GATE_LO, SUN_GATE_HI, sunY);
  const strength = clamp01(amount) * gate;

  // The rise to the middle of the layer, and the distance along the sun ray that covers it.
  const midAltitudeM = baseAltitudeM + layerThicknessM * 0.5;
  const rise = Math.max(midAltitudeM - y, 50);
  const projectionM = Math.min(rise / Math.max(sunY, 0.05), MAX_PROJECTION_M);

  const hitX = x + sunX * projectionM;
  const hitZ = z + sunZ * projectionM;

  const px = (hitX + windOffsetX) * SAMPLE_SCALE;
  const pz = (hitZ + windOffsetZ) * SAMPLE_SCALE;

  const weather = sampleVolume(shape, shapeSize, 0, px, COVERAGE_SLICE_Y, pz);
  const threshold = coverageThreshold(coverage, weather);
  const opacity = coverageOpacity(coverage);
  const topGradient = 1 + (TOP_TAP_CUMULUS_GRADIENT - 1) * clamp01(convection);

  let column = 0;
  for (let i = 0; i < LAYER_TAPS.length; i++) {
    const vertical = LAYER_TAPS[i] * VERTICAL_SCALE;
    const gradient = i === LAYER_TAPS.length - 1 ? topGradient : 1;
    const r = sampleVolume(shape, shapeSize, 0, px, vertical, pz);
    const g = sampleVolume(shape, shapeSize, 1, px, vertical, pz);
    const b = sampleVolume(shape, shapeSize, 2, px, vertical, pz);
    const a = sampleVolume(shape, shapeSize, 3, px, vertical, pz);
    const lowFrequency = g * 0.625 + b * 0.25 + a * 0.125;
    const shaped = clamp01(remap(r, lowFrequency - 1, 1, 0, 1));
    column += clamp01(remap(shaped * gradient, threshold, threshold + DENSITY_BAND, 0, 1));
  }
  const meanColumn = column / LAYER_TAPS.length;
  const sharp = meanColumn * opacity * EROSION_ALLOWANCE;

  // Penumbra: blur toward the layer's mean opacity with distance to the caster.
  const blend = Math.min(MAX_PENUMBRA_BLEND, projectionM / PENUMBRA_RANGE_M);
  const density = sharp + (coverage * 0.45 * opacity * EROSION_ALLOWANCE - sharp) * blend;

  const slant = Math.min(MAX_SLANT, 1 / Math.max(sunY, 1e-3));
  const opticalDepth = density * layerThicknessM * EXTINCTION_PER_M * slant;
  const direct = Math.max(Math.exp(-opticalDepth), MIN_DIRECT);

  return {
    factor: 1 + (direct - 1) * strength,
    hitX,
    hitZ,
    projectionM,
    density,
  };
}

/**
 * The uniforms and the TSL node that put {@link cloudShadowFactor} on the GPU.
 *
 * Constructed and driven by {@link VolumetricClouds}, which already owns every input; consumed by
 * the terrain and the water, which call {@link factorAt} once each when they build their materials.
 */
export class CloudShadow {
  /** 0 = off. A uniform rather than a rebuild, so a tier change costs no shader recompile. */
  private readonly amount = uniform(0);
  private readonly coverage = uniform(0.4);
  private readonly convection = uniform(0.8);
  private readonly baseAltitude = uniform(1_220);
  private readonly layerThickness = uniform(900);
  private readonly windOffset = uniform(new Vector3(0, 0, 0));
  private readonly sunDirection = uniform(new Vector3(0, 1, 0));

  private readonly shape: Data3DTexture;
  /**
   * The factor evaluated at `positionWorld`, built once on first use.
   *
   * Shared rather than rebuilt per material so every ground surface generates *identical* shader
   * code — the same reason the terrain factory shares its uniform nodes. A per-material subgraph
   * would be the same arithmetic and a different program, and the terrain alone has one material
   * per resident tile.
   */
  private worldFactor: Node<'float'> | null = null;

  constructor(options: CloudShadowOptions) {
    this.shape = options.shape;
  }

  /**
   * Multiply a ground material's received sunlight by the cloud shadow.
   *
   * Wire it unconditionally: with {@link setAmount} at 0 the node evaluates to exactly 1, so the
   * tier decides at runtime and no material is ever rebuilt for it.
   *
   * The hook only runs where the sun is casting shadows at all — it lives inside three's
   * `ShadowNode.setup`. `castShadows` is only set on tiers that also have `shadows.enabled`, and
   * `quality.test.ts` holds that invariant so a future tier edit cannot quietly break this.
   */
  applyTo(material: NodeMaterial): void {
    this.worldFactor ??= this.factorAt(positionWorld);
    const factor = this.worldFactor;
    /*
     * `@types/three` declares `receivedShadowNode` as a zero-argument function. It is not: three
     * calls it as `receivedShadowNode(node)` and uses the return value in place of the shadow
     * term (`ShadowNode.setup`). The cast states the real contract once, here, rather than at
     * every call site.
     */
    (material as unknown as { receivedShadowNode: (shadow: Node<'float'>) => Node<'float'> })
      .receivedShadowNode = (shadow) => shadow.mul(factor);
  }

  /**
   * Fraction of direct sunlight reaching `worldPosition`, as a node — 1 when unshadowed.
   *
   * Multiply the direct sun term by this. It is exactly 1 everywhere when {@link setAmount} is 0,
   * so a material can wire it unconditionally and let the tier decide.
   *
   * Explicit level 0 on both fetches: the volumes have no mipmaps, so it is the only correct
   * level, and asking for it removes the screen-space derivative — which matters because this is
   * evaluated inside three's shadow code, where the control flow is not guaranteed uniform and a
   * derivative-taking sample is a WGSL validation failure that surfaces as a pipeline that is
   * simply never created. See the same note in `volumetricClouds.buildRaymarch`.
   */
  factorAt(worldPosition: Node<'vec3'>): Node<'float'> {
    return Fn(() => {
      const result = float(1).toVar();

      /*
       * The four volume fetches, behind a uniform branch.
       *
       * Without it, Medium pays for High's feature: `receivedShadowNode` is compiled into every
       * ground material on every tier that has shadows at all, and the fetches would execute
       * whatever `amount` is set to. The branch is on a uniform, so every invocation in a wave
       * takes the same side of it and it costs nothing beyond the compare.
       */
      If(this.amount.greaterThan(0), () => {
        const sun = vec3(this.sunDirection);
        const drift = vec3(this.windOffset);

        // Below a few degrees of elevation the projection distance diverges and there is no direct
        // sun left worth modulating; fade the whole effect out rather than projecting nonsense.
        const gate = smoothstep(float(SUN_GATE_LO), float(SUN_GATE_HI), sun.y);
        const strength = this.amount.mul(gate);

        const midAltitude = this.baseAltitude.add(this.layerThickness.mul(0.5));
        const rise = max(midAltitude.sub(worldPosition.y), float(50));
        const projection = min(rise.div(max(sun.y, float(0.05))), float(MAX_PROJECTION_M)).toVar();

        const hit = vec2(
          worldPosition.x.add(sun.x.mul(projection)),
          worldPosition.z.add(sun.z.mul(projection)),
        );
        const p = hit.add(vec2(drift.x, drift.z)).mul(SAMPLE_SCALE).toVar();

        const weather = texture3D(this.shape, vec3(p.x, float(COVERAGE_SLICE_Y), p.y), 0).r;
        // The same threshold the march cuts against — see `coverage.ts`. One field, one rule.
        const threshold = coverageThresholdNode(this.coverage, weather).toVar();
        const opacity = coverageOpacityNode(this.coverage).toVar();
        const topGradient = mix(float(1), float(TOP_TAP_CUMULUS_GRADIENT), this.convection);

        // Three taps down the column. Unrolled on the host, so this is straight-line shader code
        // with no loop — the trip count is a constant and the taps differ only in a literal.
        const column = float(0).toVar();
        for (let i = 0; i < LAYER_TAPS.length; i++) {
          const vertical = float(LAYER_TAPS[i] * VERTICAL_SCALE);
          const gradient = i === LAYER_TAPS.length - 1 ? topGradient : float(1);
          const base = texture3D(this.shape, vec3(p.x, vertical, p.y), 0);
          const lowFrequency = base.g.mul(0.625).add(base.b.mul(0.25)).add(base.a.mul(0.125));
          const shaped = clamp(
            remapNode(base.r, lowFrequency.sub(1), float(1), float(0), float(1)),
            0,
            1,
          );
          column.addAssign(
            clamp(
              remapNode(
                shaped.mul(gradient),
                threshold,
                threshold.add(DENSITY_BAND),
                float(0),
                float(1),
              ),
              0,
              1,
            ),
          );
        }
        const sharp = column.div(LAYER_TAPS.length).mul(opacity).mul(EROSION_ALLOWANCE);

        const blend = min(float(MAX_PENUMBRA_BLEND), projection.div(PENUMBRA_RANGE_M));
        const density = mix(sharp, this.coverage.mul(opacity).mul(0.45 * EROSION_ALLOWANCE), blend);

        const slant = min(float(MAX_SLANT), float(1).div(max(sun.y, float(1e-3))));
        const opticalDepth = density.mul(this.layerThickness).mul(EXTINCTION_PER_M).mul(slant);
        const direct = max(exp(opticalDepth.negate()), float(MIN_DIRECT));

        result.assign(mix(float(1), direct, strength));
      });

      return result;
    })();
  }

  /** 0 turns the effect off exactly; 1 is full strength. */
  setAmount(amount: number): void {
    this.amount.value = clamp01(amount);
  }

  get amountValue(): number {
    return this.amount.value as number;
  }

  setLayer(coverage: number, baseAltitudeM: number, thicknessM: number, convection: number): void {
    this.coverage.value = coverage;
    this.baseAltitude.value = baseAltitudeM;
    this.layerThickness.value = Math.max(120, thicknessM);
    this.convection.value = clamp01(convection);
  }

  setSunDirection(direction: Vector3): void {
    (this.sunDirection.value as Vector3).copy(direction).normalize();
  }

  /** The layer's advection offset [m]. The shadow drifts because this does. */
  setWindOffset(x: number, z: number): void {
    (this.windOffset.value as Vector3).set(x, 0, z);
  }
}

/** `smoothstep`, on the CPU, matching TSL's edge order. */
function smoothstepScalar(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Exposed so `cloudShadow.test.ts` can pin them against the raymarch's own constants. */
export {
  SAMPLE_SCALE,
  COVERAGE_SLICE_Y,
  LAYER_TAPS,
  VERTICAL_SCALE,
  EXTINCTION_PER_M,
  EROSION_ALLOWANCE,
  MIN_DIRECT,
  MAX_SLANT,
  SUN_GATE_LO,
  SUN_GATE_HI,
  MAX_PROJECTION_M,
};
